// Imports for external libraries go here.
import { FC, useEffect, useState } from 'react';
import clsx from 'clsx';

import { StyledSignInButton, AccountDrawer, StyledSignInDrawer } from './SignInDrawer.styles';
import { AccountPanel } from './AccountPanel';
import { SignInDrawerProps } from './SignInDrawer.types';
import { HeaderLogoProps, canUseDOM, eventUtil } from '@marriott/mi-ui-library';
import { MemberPanelProps } from './MemberPanel';
import { SignInFormProps } from './SignInForm';
import { getUserDetails, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { getClickTrackValue } from '../../utils/TrackingPropertyUtils';
import {
  DEFAULT_TRACKING_VALUES,
  ACCOUNT_MODAL_CLOSE_EVENT,
  ACCOUNT_MODAL_OPEN_EVENT,
  ACCOUNT_FLYOUT_MODAL_ID,
  CLOSE_DRAWER_EVENT,
} from '../../utils/constants';
import { useNavContext } from '../../organisms/NavigationContainer/NavigationContextProvider';

// Use named rather than default exports.
export const SignInDrawer: FC<SignInDrawerProps> = props => {
  const { logoUrl, logoImageAltText, logoType, restProps, renderUtilityLink, ctaClass, isAccountFlyout } = props;
  const {
    accountCancelledMessage,
    accountStoppedMessage,
    alreadyCombinedError,
    joinNowLabel,
    becomeMemberLabel,
    becomeMemberUrl,
    signInLabel,
    memberNumberLabel,
    loginPasswordLabel,
    loginPasswordPlaceHolder,
    remeberMeLabel,
    signInButtonLabel,
    signInButtonUrl,
    joinNowSubHeading,
    forgotPasswordLinkLabel,
    forgotPasswordLinkPath,
    activeAccountLinkUrl,
    activateAccountLinkLabel,
    clearRememberedAccountLabel,
    clearRememberedAccountUrl,
    welcomeText,
    signInSubHeading,
    pointsLabel,
    buyPointsLinkLabel,
    buyPointsLinkpath,
    logoutLinkLabel,
    logoutLinkPath,
    warningMessage,
    credentialsWarningMessage,
    lockoutWarningMessage,
    lockedWarningMessage,
    auditWarningMessage,
    signInPageWarnMessage,
    invalidEmailOrMemberError,
    otpLockedOutErrorMessage,
    uxlFailureErrorMessage,
  } = restProps.resourceProperties ?? {};

  const { offset, navRef, isSignInDrawerOpen, setIsSignInDrawerOpen } = useNavContext();

  const [top, setTop] = useState<number | string>(offset);

  const userProfileData = useUserDetailsGlobalStore(state => state);
  const isRememberedUser = useUserDetailsGlobalStore(state => state.rememberedUser);
  const isSignedInUser = useUserDetailsGlobalStore(state => state.isSignedInUser);
  const renderAccountAnonymousFlyout = !isSignedInUser && isAccountFlyout;

  // TODO: When Signed In story is implemented we should be using Zustand to access data from store.
  const togglePanel = () => {
    if (renderAccountAnonymousFlyout) {
      if (isSignInDrawerOpen) {
        document.dispatchEvent(new CustomEvent(ACCOUNT_MODAL_CLOSE_EVENT, {}));
      } else {
        document.dispatchEvent(
          new CustomEvent(ACCOUNT_MODAL_OPEN_EVENT, {
            detail: {
              modalType: ACCOUNT_FLYOUT_MODAL_ID,
              isSwap: !isRememberedUser,
              isFlyOut: true,
              isGlobalHeaderSignIn: true,
            },
          })
        );
      }
    }
    setIsSignInDrawerOpen(prevState => {
      return !prevState;
    });
  };
  const memberName = userProfileData?.headerSubtext?.consumerName;

  const recognizedUserButtonText = isRememberedUser ? `${signInLabel}, ${memberName}` : memberName;

  const isAuthenticated = isSignedInUser || isRememberedUser;

  const buttonText = isAuthenticated ? recognizedUserButtonText : restProps?.resourceProperties?.['cq:panelTitle'];
  const signInButtonClass = `nav---account${isAuthenticated ? '-alt-active' : ''}`;

  const signInErrorMessages = {
    alreadyCombinedError,
    accountCancelledMessage,
    accountStoppedMessage,
    warningMessage,
    credentialsWarningMessage,
    lockoutWarningMessage,
    lockedWarningMessage,
    auditWarningMessage,
    signInPageWarnMessage,
    invalidEmailOrMemberError,
    otpLockedOutErrorMessage,
    uxlFailureErrorMessage,
    pwrdErrorMessage: restProps.pwrdErrorMessage,
    emailErrorLabel: restProps.emailErrorLabel,
    errorForUserName: restProps.errorForUserName ?? '',
    errorForPassword: restProps.errorForPassword ?? '',
  };
  const ariaLabelId = 'account-panel-label';

  const signInFormProps: SignInFormProps = {
    signInLabel,
    memberNumberLabel,
    loginPasswordLabel,
    loginPasswordPlaceHolder,
    remeberMeLabel,
    signInButtonLabel,
    signInButtonUrl,
    forgotPasswordLinkLabel,
    forgotPasswordLinkPath,
    activateAccountLinkLabel,
    activeAccountLinkUrl,
    clearRememberedAccountLabel,
    clearRememberedAccountUrl,
    welcomeText,
    signInSubHeading,
    signInErrorMessages,
    joinNowLabel,
    joinNowSubHeading,
    becomeMemberLabel,
    becomeMemberUrl,
    ariaLabelId,
  };
  const memberPanelProps: MemberPanelProps = {
    joinNowLabel,
    joinNowSubHeading,
    becomeMemberLabel,
    becomeMemberUrl,
    pointsLabel,
    buyPointsLinkLabel,
    buyPointsLinkpath,
    memberPanelCardLinks: restProps?.listItems,
    logoutLinkLabel,
    logoutLinkPath,
    memberBenefitsLabel: restProps.memberBenefitsLabel,
    ariaLabelId,
  };
  const headerLogoProps: HeaderLogoProps = {
    logoUrl,
    logoImageAltText,
    logoType,
    customClassName: 'panelLogo',
    isClickable: false,
  };

  const clickTrackValue = getClickTrackValue({
    trackingProperties: {
      trackingContentPosition: restProps?.trackingProperties?.trackingContentPosition,
    },
    url: restProps['cq:panelUrl'],
    fallbacks: {
      description: restProps?.resourceProperties?.['cq:panelTitle'],
      position: DEFAULT_TRACKING_VALUES.GLOBAL_NAV,
    },
  });

  const navBottom = navRef?.current?.getBoundingClientRect().bottom;

  useEffect(() => {
    if (!canUseDOM && !isAccountFlyout) {
      return;
    }

    const handleCloseDrawer = () => setIsSignInDrawerOpen(false);
    eventUtil.on(CLOSE_DRAWER_EVENT, handleCloseDrawer);

    return () => {
      eventUtil.remove(CLOSE_DRAWER_EVENT, handleCloseDrawer);
    };
  }, [setIsSignInDrawerOpen]);

  useEffect(() => {
    if (canUseDOM) {
      // Populate the userDetailsStore with user information for nav components to consume.
      // TODO: This should be called higher up in the global nav, since other parts
      // of the global nav consume user details information.

      getUserDetails(true);
    }
  }, []);

  useEffect(() => {
    // Getting the difference between top of window and bottom of the navbar. It's not exact, but it allows for the positioning of the signin panel at the top of each given view
    const offsetDifference = window.scrollY - navBottom;
    if (canUseDOM) {
      // TODO: fix nested if statement
      if (offsetDifference > 0) {
        setTop(0);
      } else {
        setTop('inherit');
      }
    }
  }, [navBottom]);

  return (
    <StyledSignInDrawer>
      <StyledSignInButton
        aria-label={buttonText}
        data-component-name="m-common-static-SignInDrawer"
        data-testid="common-static-SignInDrawer"
        className={clsx('t-label-inverse-xs custom_click_track', ctaClass, {
          unauthenticated: !isAuthenticated,
          'rememberedBtn p-0 d-flex align-items-center': isAuthenticated,
        })}
        onClick={togglePanel}
        data-custom_click_track_value={clickTrackValue}
      >
        {renderUtilityLink(restProps, signInButtonClass, buttonText, 'signin')}
        {isAuthenticated && <span className="icon-arrow-right d-none d-lg-inline-block"></span>}
      </StyledSignInButton>
      {!renderAccountAnonymousFlyout && isSignInDrawerOpen && (
        <AccountDrawer
          isOpen={isSignInDrawerOpen}
          className="standard t-background-color"
          style={{ top: -offset, height: `calc(100vh - ${-offset}px)` }}
        >
          {/* styling for the dialog */}
          <style>
            {`
          [role='dialog'] {
            top: ${top};
            height: inherit;
            text-wrap: wrap;
          }
        `}
          </style>
          <AccountPanel
            togglePanel={togglePanel}
            headerLogoProps={headerLogoProps}
            memberPanelProps={memberPanelProps}
            signInFormProps={signInFormProps}
            ariaLabelId={ariaLabelId}
          />
        </AccountDrawer>
      )}
    </StyledSignInDrawer>
  );
};
